import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import ArticleModal from '../components/ArticleModal'
import ContactArticleModal from '../components/ContactArticleModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faTrash, faFloppyDisk, faPlus, faUpload, faFileImport, faTags, faList, faHandPointer } from '@fortawesome/free-solid-svg-icons'
import { ResourcesSearchProvider, useResourcesSearch } from '../contexts/ResourcesSearchContext'
import {
  resourcesService,
  type ManagedResourceItem,
  type ResourceContactEntry,
  type PolicyMarkdownPreview,
  type PolicyMarkdownSplitLevel,
  policyHeadingDepthLabel,
} from '../services/resourcesService'
import { displayEmail, displayPhone } from '../utils/contactDisplay'
import { useAuth } from '../contexts/AuthContext'
import { useResourcesKioskSync } from '../hooks/useResourcesKioskSync'

type Article = {
  id: string
  title: string
  content: string
  type: 'FAQ' | 'Policy' | 'Legal'
  batchTag?: string
  tags?: string[]
}

type ContactArticle = {
  type: 'Contact'
  employeeId: string
  name: string
  email: string
  phone?: string
  role?: string
  organization?: string
  notes?: string
}

type Resource = Article | ContactArticle

type ResourcesTab = 'resources' | 'management'
type ManagementPanel = 'entries' | 'import' | 'batches'

type ArticleType = 'All' | 'FAQ' | 'Contact' | 'Policy' | 'Legal'

type MarkdownSplitChoice = 'auto' | PolicyMarkdownSplitLevel

const MARKDOWN_SPLIT_OPTIONS: { value: MarkdownSplitChoice; label: string }[] = [
  { value: 'auto', label: 'Auto-detect (recommended)' },
  { value: 1, label: 'H1 (#) — split on single-hash headings' },
  { value: 2, label: 'H2 (##) — split on ## headings' },
  { value: 3, label: 'H3 (###) — split on ### headings' },
  { value: 4, label: 'H4 (####)' },
  { value: 5, label: 'H5 (#####)' },
  { value: 6, label: 'H6 (######)' },
]

function resourceItemKey(resource: Resource): string {
  if (resource.type === 'Contact') {
    return `${resource.type}:${encodeURIComponent(resource.employeeId || resource.email || resource.name)}`
  }
  return `${resource.type}:${resource.id}`
}

/** Prefer newest contact when the same person appears under multiple employeeIds / emails. */
function dedupeContactsForDirectory(contacts: ResourceContactEntry[]): ResourceContactEntry[] {
  const byEmployeeId = new Map<string, ResourceContactEntry>()
  for (const c of contacts) {
    const id = String(c.employeeId || '').trim()
    if (!id) continue
    const prev = byEmployeeId.get(id)
    if (!prev || contactRecency(c) >= contactRecency(prev)) {
      byEmployeeId.set(id, c)
    }
  }

  const byIdentity = new Map<string, ResourceContactEntry>()
  for (const c of byEmployeeId.values()) {
    const emailKey = (displayEmail(c) || '').trim().toLowerCase()
    const identity = emailKey || `id:${String(c.employeeId).trim().toLowerCase()}`
    const prev = byIdentity.get(identity)
    if (!prev || contactRecency(c) >= contactRecency(prev)) {
      byIdentity.set(identity, c)
    }
  }
  return Array.from(byIdentity.values())
}

function contactRecency(c: ResourceContactEntry): number {
  const raw = c.updatedAt || c.createdAt
  if (!raw) return 0
  const t = Date.parse(raw)
  return Number.isFinite(t) ? t : 0
}

function toContactArticle(c: ResourceContactEntry): ContactArticle {
  return {
    type: 'Contact',
    employeeId: c.employeeId,
    name: c.preferredName ? `${c.preferredName} ${c.name.split(' ').slice(1).join(' ')}` : c.name,
    role: c.position,
    email: displayEmail(c),
    phone: displayPhone(c),
  }
}

function parseCommaSeparatedTags(s: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const part of s.split(',')) {
    const t = part.trim()
    if (!t) continue
    const k = t.toLowerCase()
    if (seen.has(k)) continue
    seen.add(k)
    out.push(t)
  }
  return out
}

interface ResourcesPageContentProps {
}

const ResourcesPageContent: React.FC<ResourcesPageContentProps> = () => {
  const navigate = useNavigate()
  const { search, setSearch } = useResourcesSearch()
  const { loading: authLoading, hasPermission } = useAuth()
  const [filter, setFilter] = useState<ArticleType>('All')
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null)
  const [selectedContact, setSelectedContact] = useState<ContactArticle | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [contactModalOpen, setContactModalOpen] = useState(false)
  const [faqPolicies, setFaqPolicies] = useState<ManagedResourceItem[]>([])
  const [importBatchTags, setImportBatchTags] = useState<Record<string, string>>({})
  const [batchTagEdits, setBatchTagEdits] = useState<Record<string, string>>({})
  const [batchTagSavingId, setBatchTagSavingId] = useState<string | null>(null)
  const [resourcesLoading, setResourcesLoading] = useState(true)
  const [resourcesError, setResourcesError] = useState<string | null>(null)
  const [resourceSaving, setResourceSaving] = useState(false)
  const [resourceSaveError, setResourceSaveError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [formType, setFormType] = useState<'FAQ' | 'Policy' | 'Legal'>('FAQ')
  const [formTitle, setFormTitle] = useState('')
  const [formContent, setFormContent] = useState('')
  const [formPolicyTags, setFormPolicyTags] = useState('')
  const [tab, setTab] = useState<ResourcesTab>('resources')
  const [managementPanel, setManagementPanel] = useState<ManagementPanel>('entries')
  const [manageListSearch, setManageListSearch] = useState('')
  const [manageListFilter, setManageListFilter] = useState<'All' | 'FAQ' | 'Policy' | 'Legal'>('All')
  const [mdSplitChoice, setMdSplitChoice] = useState<MarkdownSplitChoice>('auto')
  const [mdRaw, setMdRaw] = useState<string | null>(null)
  const [mdFileName, setMdFileName] = useState<string | null>(null)
  const [mdPreview, setMdPreview] = useState<PolicyMarkdownPreview | null>(null)
  const [mdImportChecked, setMdImportChecked] = useState<boolean[]>([])
  const [mdImportError, setMdImportError] = useState<string | null>(null)
  const [mdImportBusy, setMdImportBusy] = useState(false)
  const showManagement = hasPermission('resourcesManager')

  // Permission guard - redirect unauthorized users
  useEffect(() => {
    if (!authLoading && !hasPermission('resources')) {
      navigate('/', { replace: true });
    }
  }, [authLoading, hasPermission, navigate]);

  useEffect(() => {
    if (tab === 'management' && !showManagement) setTab('resources')
  }, [showManagement, tab])

  const news = [
    'LTS wins new telecom contract',
    'Quarterly safety training announced',
    'Employee of the Month: Jane Doe',
  ]

  const recentActivity = [
    'User John submitted a new form',
    'Site 12 updated by Admin',
    'GIS data refreshed',
    'Resource "Safety Manual" added',
  ]

  const [contacts, setContacts] = useState<ResourceContactEntry[]>([])

  useEffect(() => {
    resourcesService.listContacts().then(setContacts).catch(() => setContacts([]))
  }, [])

  const loadFaqPolicies = useCallback(async () => {
    setResourcesLoading(true)
    setResourcesError(null)
    try {
      const { items, importBatchTags: batchTags } = await resourcesService.listFaqPolicies()
      setFaqPolicies(items)
      setImportBatchTags(batchTags)
    } catch (err: any) {
      setResourcesError(err?.message || 'Failed to load FAQ and policy content')
      setFaqPolicies([])
    } finally {
      setResourcesLoading(false)
    }
  }, [])

  useEffect(() => {
    loadFaqPolicies()
  }, [loadFaqPolicies])

  useEffect(() => {
    if (!mdRaw) {
      setMdPreview(null)
      setMdImportChecked([])
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const prev = await resourcesService.previewPolicyMarkdown(
          mdRaw,
          mdSplitChoice === 'auto' ? undefined : mdSplitChoice,
        )
        if (!cancelled) {
          setMdPreview(prev)
          setMdImportChecked(prev.items.map(() => true))
          setMdImportError(null)
        }
      } catch (err: any) {
        if (!cancelled) {
          setMdPreview(null)
          setMdImportChecked([])
          setMdImportError(err?.message || 'Preview failed')
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [mdRaw, mdSplitChoice])

  const importBatches = useMemo(() => {
    const map = new Map<string, { importBatchId: string; sourceFileName?: string; count: number }>()
    for (const item of faqPolicies) {
      if (!item.importBatchId) continue
      const cur = map.get(item.importBatchId) || {
        importBatchId: item.importBatchId,
        sourceFileName: item.sourceFileName,
        count: 0,
      }
      cur.count += 1
      if (item.sourceFileName) cur.sourceFileName = item.sourceFileName
      map.set(item.importBatchId, cur)
    }
    return Array.from(map.values())
  }, [faqPolicies])

  const managedEntriesFiltered = useMemo(() => {
    let items = [...faqPolicies]
    if (manageListFilter !== 'All') {
      items = items.filter((item) => item.type === manageListFilter)
    }
    const q = manageListSearch.trim().toLowerCase()
    if (q) {
      items = items.filter((item) => {
        const tagHaystack = [item.batchTag, ...(item.tags || [])].filter(Boolean).join(' ').toLowerCase()
        return (
          item.title.toLowerCase().includes(q) ||
          item.content.toLowerCase().includes(q) ||
          item.type.toLowerCase().includes(q) ||
          tagHaystack.includes(q)
        )
      })
    }
    return items
  }, [faqPolicies, manageListFilter, manageListSearch])

  const allResources = useMemo(() => {
    const faqPolicyResources: Resource[] = faqPolicies.map((item) => ({
      id: item.id,
      type: item.type,
      title: item.title,
      content: item.content,
      ...(item.type === 'Policy'
        ? { batchTag: item.batchTag, tags: item.tags }
        : {}),
    }))
    const contactResources: Resource[] = dedupeContactsForDirectory(contacts).map(toContactArticle)
    return [...faqPolicyResources, ...contactResources]
  }, [contacts, faqPolicies])

  const pendingDisplayKeyRef = useRef<string | null>(null)

  const openResourceByKey = useCallback(
    (selectedKey: string | null) => {
      if (!selectedKey) {
        pendingDisplayKeyRef.current = null
        setModalOpen(false)
        setSelectedArticle(null)
        setContactModalOpen(false)
        setSelectedContact(null)
        return
      }
      const match = allResources.find((r) => resourceItemKey(r) === selectedKey)
      if (!match) {
        // Catalog may still be loading on the display.
        pendingDisplayKeyRef.current = selectedKey
        return
      }
      pendingDisplayKeyRef.current = null
      if (match.type === 'Contact') {
        setModalOpen(false)
        setSelectedArticle(null)
        setSelectedContact(match)
        setContactModalOpen(true)
      } else {
        setContactModalOpen(false)
        setSelectedContact(null)
        setSelectedArticle(match)
        setModalOpen(true)
      }
    },
    [allResources],
  )

  useEffect(() => {
    if (pendingDisplayKeyRef.current && allResources.length > 0) {
      openResourceByKey(pendingDisplayKeyRef.current)
    }
  }, [allResources, openResourceByKey])

  const onRemoteSelection = useCallback(
    (selectedKey: string | null) => {
      openResourceByKey(selectedKey)
    },
    [openResourceByKey],
  )

  const { isDisplayKiosk, isControllerKiosk, controllerSelectedKey, publishSelection } =
    useResourcesKioskSync({ onRemoteSelection })

  const filteredArticles = useMemo(() => {
    let articles: Resource[] = [...allResources]
    if (filter !== 'All') {
      articles = articles.filter((a) => a.type === filter)
    }
    if (search.trim()) {
      const s = search.toLowerCase()
      articles = articles.filter((a) =>
        (a.type === 'Contact'
          ? (
              a.name.toLowerCase().includes(s) ||
              (a.role?.toLowerCase().includes(s) ?? false) ||
              (a.organization?.toLowerCase().includes(s) ?? false) ||
              (a.email.toLowerCase().includes(s) ?? false) ||
              (a.phone?.toLowerCase().includes(s) ?? false) ||
              (a.notes?.toLowerCase().includes(s) ?? false)
            )
          : (() => {
              const art = a as Article
              const tagHaystack = [art.batchTag, ...(art.tags || [])].filter(Boolean).join(' ').toLowerCase()
              return (
                art.title.toLowerCase().includes(s) ||
                art.content.toLowerCase().includes(s) ||
                tagHaystack.includes(s)
              )
            })()
        )
      )
    }
    return articles
  }, [allResources, search, filter])

  const grouped = useMemo(() => {
    return ['FAQ', 'Contact', 'Policy', 'Legal'].reduce((acc, type) => {
      acc[type] = filteredArticles.filter(a => a.type === type)
      return acc
    }, {} as Record<string, Resource[]>)
  }, [filteredArticles])

  const handleOpenArticle = useCallback(
    (article: Resource) => {
      const key = resourceItemKey(article)
      if (isControllerKiosk) {
        // Controller picks; display opens. Toggle off if re-tapping the same item.
        if (controllerSelectedKey === key) {
          publishSelection(null)
        } else {
          publishSelection(key)
        }
        return
      }
      if (article.type === 'Contact') {
        setModalOpen(false)
        setSelectedArticle(null)
        setSelectedContact(article)
        setContactModalOpen(true)
      } else {
        setContactModalOpen(false)
        setSelectedContact(null)
        setSelectedArticle(article)
        setModalOpen(true)
      }
    },
    [isControllerKiosk, controllerSelectedKey, publishSelection],
  )

  // Show loading while checking permissions
  if (authLoading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-primary animate-spin" />
          <span className="text-lg">Loading...</span>
        </div>
      </div>
    );
  }

  // Don't render anything if user doesn't have permission (will redirect)
  if (!hasPermission('resources')) {
    return null;
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setSelectedArticle(null)
  }
  const handleCloseContactModal = () => {
    setContactModalOpen(false)
    setSelectedContact(null)
  }

  const resetManagerForm = () => {
    setEditingId(null)
    setFormType('FAQ')
    setFormTitle('')
    setFormContent('')
    setFormPolicyTags('')
    setResourceSaveError(null)
  }

  const startEditEntry = (entry: ManagedResourceItem) => {
    setEditingId(entry.id)
    setFormType(entry.type)
    setFormTitle(entry.title)
    setFormContent(entry.content)
    setFormPolicyTags(entry.type === 'Policy' && entry.tags?.length ? entry.tags.join(', ') : '')
    setResourceSaveError(null)
  }

  const handleSaveEntry = async () => {
    const title = formTitle.trim()
    const content = formContent.trim()
    if (!title || !content) {
      setResourceSaveError('Title and content are required.')
      return
    }

    setResourceSaving(true)
    setResourceSaveError(null)
    try {
      const policyTagsPayload =
        formType === 'Policy' ? parseCommaSeparatedTags(formPolicyTags) : undefined
      if (editingId) {
        await resourcesService.updateFaqPolicy({
          id: editingId,
          type: formType,
          title,
          content,
          ...(formType === 'Policy' ? { tags: policyTagsPayload } : {}),
        })
      } else {
        await resourcesService.createFaqPolicy({
          type: formType,
          title,
          content,
          ...(formType === 'Policy' ? { tags: policyTagsPayload } : {}),
        })
      }
      await loadFaqPolicies()
      resetManagerForm()
    } catch (err: any) {
      setResourceSaveError(err?.message || 'Failed to save resource content.')
    } finally {
      setResourceSaving(false)
    }
  }

  const handleDeleteEntry = async (entry: ManagedResourceItem) => {
    const confirmed = window.confirm(`Delete "${entry.title}"?`)
    if (!confirmed) return
    setResourceSaving(true)
    setResourceSaveError(null)
    try {
      await resourcesService.deleteFaqPolicy(entry.id)
      await loadFaqPolicies()
      if (editingId === entry.id) resetManagerForm()
    } catch (err: any) {
      setResourceSaveError(err?.message || 'Failed to delete resource content.')
    } finally {
      setResourceSaving(false)
    }
  }

  const isMarkdownFile = (f: File) => {
    const n = f.name.toLowerCase()
    return n.endsWith('.md') || n.endsWith('.markdown')
  }

  const handlePolicyMarkdownPick = (files: FileList | null) => {
    if (!files?.length) return
    const file = files[0]
    if (!isMarkdownFile(file)) {
      setMdImportError('Only Markdown files (.md or .markdown) are allowed.')
      setMdRaw(null)
      setMdFileName(null)
      setMdPreview(null)
      setMdImportChecked([])
      return
    }
    setMdImportError(null)
    setMdFileName(file.name)
    const reader = new FileReader()
    reader.onload = () => {
      setMdRaw(typeof reader.result === 'string' ? reader.result : null)
    }
    reader.onerror = () => {
      setMdImportError('Failed to read file')
      setMdRaw(null)
    }
    reader.readAsText(file)
  }

  const clearMarkdownImport = () => {
    setMdRaw(null)
    setMdFileName(null)
    setMdPreview(null)
    setMdImportChecked([])
    setMdImportError(null)
  }

  const toggleMdImportChecked = (idx: number) => {
    setMdImportChecked((prev) => {
      if (idx < 0 || idx >= prev.length) return prev
      return prev.map((c, i) => (i === idx ? !c : c))
    })
  }

  const handleImportMarkdown = async () => {
    if (!mdRaw || !mdPreview?.items.length) return
    const selectedIndexes = mdPreview.items.map((_, i) => i).filter((i) => mdImportChecked[i])
    if (!selectedIndexes.length) return
    setMdImportBusy(true)
    setMdImportError(null)
    try {
      await resourcesService.importPolicyMarkdown({
        markdown: mdRaw,
        sourceFileName: mdFileName || undefined,
        splitLevel: mdSplitChoice === 'auto' ? undefined : mdSplitChoice,
        selectedIndexes,
      })
      await loadFaqPolicies()
      clearMarkdownImport()
      setManagementPanel('entries')
    } catch (err: any) {
      setMdImportError(err?.message || 'Import failed')
    } finally {
      setMdImportBusy(false)
    }
  }

  const handleSaveImportBatchTag = async (importBatchId: string) => {
    const raw =
      importBatchId in batchTagEdits ? batchTagEdits[importBatchId] : (importBatchTags[importBatchId] ?? '')
    setBatchTagSavingId(importBatchId)
    setResourceSaveError(null)
    try {
      await resourcesService.setImportBatchTag(importBatchId, raw)
      setBatchTagEdits((prev) => {
        const next = { ...prev }
        delete next[importBatchId]
        return next
      })
      await loadFaqPolicies()
    } catch (err: any) {
      setResourceSaveError(err?.message || 'Failed to save batch tag')
    } finally {
      setBatchTagSavingId(null)
    }
  }

  const handleDeleteImportBatch = async (batch: { importBatchId: string; sourceFileName?: string; count: number }) => {
    const label = batch.sourceFileName || batch.importBatchId.slice(0, 8)
    const confirmed = window.confirm(
      `Delete all ${batch.count} policies from import "${label}"? This cannot be undone.`,
    )
    if (!confirmed) return
    setResourceSaving(true)
    setResourceSaveError(null)
    try {
      await resourcesService.deleteFaqPoliciesByImportBatch(batch.importBatchId)
      await loadFaqPolicies()
      resetManagerForm()
    } catch (err: any) {
      setResourceSaveError(err?.message || 'Bulk delete failed')
    } finally {
      setResourceSaving(false)
    }
  }

  return (
    <div className="h-[100dvh] bg-base-100 flex flex-col overflow-hidden">
      <Navbar pageTitle="RESOURCES" news={news} recentActivity={recentActivity} />
      <main className="container mx-auto px-4 py-8 flex-1 min-h-0 flex flex-col overflow-hidden">
        {showManagement && !isDisplayKiosk && (
          <div className="tabs tabs-lift mb-4 shrink-0">
            <input
              type="radio"
              name="resources_tabs"
              role="tab"
              className="tab"
              aria-label="Resources"
              checked={tab === 'resources'}
              onChange={() => setTab('resources')}
            />
            <input
              type="radio"
              name="resources_tabs"
              role="tab"
              className="tab"
              aria-label="Management"
              checked={tab === 'management'}
              onChange={() => {
                if (!showManagement) return
                setTab('management')
              }}
            />
          </div>
        )}

        {isDisplayKiosk ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 px-6 text-center">
            {!modalOpen && !contactModalOpen ? (
              <>
                <FontAwesomeIcon
                  icon={faHandPointer}
                  className="text-5xl text-base-content/30"
                  aria-hidden
                />
                <p className="max-w-md text-lg text-base-content/60">
                  Select a resource on the control screen to view it here
                </p>
              </>
            ) : null}
          </div>
        ) : null}

        {!isDisplayKiosk && tab === 'resources' && (
          <div className="w-full flex-1 min-h-0 flex flex-col">
            <div className="mb-4 shrink-0">
              <input
                type="text"
                className="input input-bordered w-full mb-4"
                placeholder="Search title, content, or tags…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                aria-label="Search resources"
              />
              <div className="flex gap-2 mb-4 flex-wrap">
                {(['All', 'FAQ', 'Contact', 'Policy', 'Legal'] as ArticleType[]).map(type => (
                  <button
                    key={type}
                    className={`btn btn-sm ${filter === type ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setFilter(type)}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
            {resourcesLoading && (
              <div className="mb-4 shrink-0">
                <div className="alert">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                  <span>Loading FAQs and policies...</span>
                </div>
              </div>
            )}
            {resourcesError && (
              <div className="mb-4 shrink-0">
                <div className="alert alert-error">
                  <span>{resourcesError}</span>
                </div>
              </div>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain pr-1 [scrollbar-gutter:stable]">
              {(['FAQ', 'Contact', 'Policy', 'Legal'] as const).map(type => (
                grouped[type].length > 0 && (
                  <div key={type} className="mb-8">
                    <h3 className="text-xl font-bold mb-2">{type === 'FAQ' ? 'FAQs' : type === 'Contact' ? 'Contacts' : type === 'Policy' ? 'Policies' : 'Legal'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      {grouped[type].map((article) => {
                        const key = resourceItemKey(article)
                        const selected = isControllerKiosk && controllerSelectedKey === key
                        return (
                          <button
                            key={key}
                            className={`block w-full text-left p-4 rounded-lg shadow ${
                              selected
                                ? 'bg-primary/15 ring-2 ring-primary'
                                : 'bg-base-200 hover:bg-base-300'
                            }`}
                            onClick={() => handleOpenArticle(article)}
                            aria-pressed={selected || undefined}
                          >
                            <div className="font-semibold">{article.type === 'Contact' ? article.name : article.title}</div>
                            {article.type === 'Policy' &&
                              ((article as Article).batchTag || (article as Article).tags?.length) ? (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {(article as Article).batchTag ? (
                                  <span className="badge badge-primary badge-sm badge-outline">{(article as Article).batchTag}</span>
                                ) : null}
                                {(article as Article).tags?.map((t) => (
                                  <span key={t} className="badge badge-ghost badge-sm">
                                    {t}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                            <div className="text-base-content/70 text-sm line-clamp-2 mt-1">
                              {article.type === 'Contact'
                                ? [article.role, article.organization, article.email, article.phone].filter(Boolean).join(' • ')
                                : article.content}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        )}

        {!isDisplayKiosk && tab === 'management' && showManagement && (
          <div className="w-full flex-1 min-h-0 flex flex-col">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4 shrink-0">
              <div>
                <h2 className="text-2xl font-bold">Resource Management</h2>
                <p className="text-sm text-base-content/70">
                  Edit entries, import Markdown policies, and manage import batches.
                </p>
              </div>
              <div className="join">
                <button
                  type="button"
                  className={`btn btn-sm join-item ${managementPanel === 'entries' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setManagementPanel('entries')}
                >
                  <FontAwesomeIcon icon={faList} className="mr-1.5" />
                  Entries
                </button>
                <button
                  type="button"
                  className={`btn btn-sm join-item ${managementPanel === 'import' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setManagementPanel('import')}
                >
                  <FontAwesomeIcon icon={faFileImport} className="mr-1.5" />
                  Import
                </button>
                <button
                  type="button"
                  className={`btn btn-sm join-item ${managementPanel === 'batches' ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setManagementPanel('batches')}
                >
                  <FontAwesomeIcon icon={faTags} className="mr-1.5" />
                  Batches{importBatches.length > 0 ? ` (${importBatches.length})` : ''}
                </button>
              </div>
            </div>

            {resourceSaveError && (
              <div className="alert alert-error mb-4 shrink-0">
                <span>{resourceSaveError}</span>
              </div>
            )}

            {managementPanel === 'entries' && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 flex-1 min-h-0">
                <div className="lg:col-span-4 xl:col-span-3 flex flex-col min-h-0 rounded-lg border border-base-300 bg-base-100">
                  <div className="p-3 border-b border-base-300 shrink-0 space-y-2">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm w-full"
                      onClick={resetManagerForm}
                      disabled={resourceSaving}
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-1.5" />
                      New entry
                    </button>
                    <input
                      type="text"
                      className="input input-bordered input-sm w-full"
                      placeholder="Search entries…"
                      value={manageListSearch}
                      onChange={(e) => setManageListSearch(e.target.value)}
                      aria-label="Search managed entries"
                    />
                    <div className="flex flex-wrap gap-1">
                      {(['All', 'FAQ', 'Policy', 'Legal'] as const).map((type) => (
                        <button
                          key={type}
                          type="button"
                          className={`btn btn-xs ${manageListFilter === type ? 'btn-primary' : 'btn-ghost'}`}
                          onClick={() => setManageListFilter(type)}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                    <div className="text-xs text-base-content/60">
                      {resourcesLoading
                        ? 'Loading…'
                        : `${managedEntriesFiltered.length} of ${faqPolicies.length} shown`}
                    </div>
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain [scrollbar-gutter:stable]">
                    {resourcesLoading ? (
                      <div className="p-4 text-sm text-base-content/70 flex items-center gap-2">
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        Loading entries…
                      </div>
                    ) : managedEntriesFiltered.length === 0 ? (
                      <div className="p-4 text-sm text-base-content/70">
                        {faqPolicies.length === 0 ? 'No FAQ/policy entries yet.' : 'No entries match your filters.'}
                      </div>
                    ) : (
                      <ul className="menu menu-sm p-1 w-full">
                        {managedEntriesFiltered.map((entry) => {
                          const selected = editingId === entry.id
                          return (
                            <li key={entry.id}>
                              <button
                                type="button"
                                className={`rounded-lg items-start ${selected ? 'active' : ''}`}
                                onClick={() => startEditEntry(entry)}
                              >
                                <div className="min-w-0 w-full text-left">
                                  <div className="flex flex-wrap gap-1 mb-1">
                                    <span className="badge badge-outline badge-xs">{entry.type}</span>
                                    {entry.type === 'Policy' && entry.batchTag ? (
                                      <span className="badge badge-primary badge-xs badge-outline">{entry.batchTag}</span>
                                    ) : null}
                                    {entry.type === 'Policy' && entry.importBatchId && !entry.batchTag ? (
                                      <span className="badge badge-ghost badge-xs">import</span>
                                    ) : null}
                                  </div>
                                  <div className="font-medium truncate">{entry.title}</div>
                                  <div className="text-xs opacity-70 line-clamp-2 font-normal">{entry.content}</div>
                                </div>
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="lg:col-span-8 xl:col-span-9 flex flex-col min-h-0 rounded-lg border border-base-300 bg-base-100">
                  <div className="px-4 py-3 border-b border-base-300 shrink-0 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-lg">
                        {editingId ? 'Edit entry' : 'Create entry'}
                      </h3>
                      <p className="text-xs text-base-content/60">
                        {editingId
                          ? 'Changes apply when you save. Select another entry from the list to switch.'
                          : 'Fill in the fields below, or select an existing entry from the list to edit.'}
                      </p>
                    </div>
                    {editingId && (
                      <button
                        type="button"
                        className="btn btn-error btn-outline btn-sm"
                        onClick={() => {
                          const entry = faqPolicies.find((e) => e.id === editingId)
                          if (entry) void handleDeleteEntry(entry)
                        }}
                        disabled={resourceSaving}
                      >
                        <FontAwesomeIcon icon={faTrash} className="mr-1.5" />
                        Delete
                      </button>
                    )}
                  </div>
                  <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain p-4 [scrollbar-gutter:stable]">
                    <div className="grid gap-4 max-w-4xl">
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium">Type</span>
                        <select
                          className="select select-bordered w-full max-w-xs"
                          value={formType}
                          onChange={(e) => setFormType(e.target.value as 'FAQ' | 'Policy' | 'Legal')}
                        >
                          <option value="FAQ">FAQ</option>
                          <option value="Policy">Policy</option>
                          <option value="Legal">Legal</option>
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium">Title</span>
                        <input
                          className="input input-bordered w-full"
                          value={formTitle}
                          onChange={(e) => setFormTitle(e.target.value)}
                          placeholder="Title"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium">Content</span>
                        <textarea
                          className="textarea textarea-bordered w-full min-h-56 font-mono text-sm"
                          value={formContent}
                          onChange={(e) => setFormContent(e.target.value)}
                          placeholder="Content (Markdown supported in the reader)"
                        />
                      </div>
                      {formType === 'Policy' && (
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-medium">Policy tags</span>
                          <span className="text-xs text-base-content/60">
                            Optional extra labels (comma-separated). Imported policies also get a batch tag under Batches.
                          </span>
                          <input
                            className="input input-bordered w-full"
                            value={formPolicyTags}
                            onChange={(e) => setFormPolicyTags(e.target.value)}
                            placeholder="e.g. safety, field-ops"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="px-4 py-3 border-t border-base-300 shrink-0 flex flex-wrap gap-2">
                    <button
                      className={`btn btn-primary ${resourceSaving ? 'btn-disabled' : ''}`}
                      onClick={handleSaveEntry}
                      disabled={resourceSaving}
                    >
                      <FontAwesomeIcon icon={editingId ? faFloppyDisk : faPlus} className="mr-2" />
                      {editingId ? 'Save changes' : 'Create entry'}
                    </button>
                    {editingId && (
                      <button className="btn btn-ghost" onClick={resetManagerForm} disabled={resourceSaving}>
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {managementPanel === 'import' && (
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain pr-1 [scrollbar-gutter:stable]">
                <div className="card bg-base-100 border border-base-300 shadow-sm max-w-4xl">
                  <div className="card-body">
                    <h3 className="card-title text-lg">
                      <FontAwesomeIcon icon={faFileImport} className="text-primary" />
                      Import policies from Markdown
                    </h3>
                    <p className="text-sm text-base-content/70">
                      Upload a <span className="font-mono">.md</span> file. The server splits it into sections from your chosen heading depth. Use the preview checkboxes to skip intro, table of contents, or any section you do not want imported.
                    </p>
                    <label className="form-control mt-2">
                      <span className="label-text mb-1">Policy heading depth</span>
                      <span className="label-text-alt text-xs text-base-content/60">
                        Choose which Markdown heading level starts a new policy. Deeper headings stay inside the previous policy. Auto picks a best guess from your file; override if the preview looks wrong.
                      </span>
                      <select
                        className="select select-bordered w-full max-w-xl mt-1"
                        value={mdSplitChoice}
                        onChange={(e) => {
                          const v = e.target.value
                          setMdSplitChoice(v === 'auto' ? 'auto' : (Number(v) as PolicyMarkdownSplitLevel))
                        }}
                      >
                        {MARKDOWN_SPLIT_OPTIONS.map((o) => (
                          <option key={String(o.value)} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <input
                      type="file"
                      accept=".md,.markdown,text/markdown"
                      className="file-input file-input-bordered w-full mt-2 max-w-xl"
                      onChange={(e) => handlePolicyMarkdownPick(e.target.files)}
                    />
                    {mdFileName && (
                      <p className="text-sm mt-2 text-base-content/70">
                        <FontAwesomeIcon icon={faUpload} className="mr-2" />
                        {mdFileName}
                        <button type="button" className="btn btn-ghost btn-xs ml-2" onClick={clearMarkdownImport}>
                          Clear
                        </button>
                      </p>
                    )}
                    {mdImportError && (
                      <div className="alert alert-error mt-2 text-sm">
                        <span>{mdImportError}</span>
                      </div>
                    )}
                    {mdPreview && (
                      <div className="mt-4 space-y-2">
                        <p className="text-sm">
                          Split at{' '}
                          <span className="font-semibold font-mono">
                            {policyHeadingDepthLabel(mdPreview.splitLevel)}
                          </span>{' '}
                          (depth H{mdPreview.splitLevel}) — <span className="font-semibold">{mdPreview.items.length}</span>{' '}
                          {mdPreview.items.length === 1 ? 'section' : 'sections'} parsed
                        </p>
                        {mdPreview.warnings.length > 0 && (
                          <div className="alert alert-warning text-sm">
                            <ul className="list-disc pl-4 m-0">
                              {mdPreview.warnings.slice(0, 8).map((w, i) => (
                                <li key={i}>{w}</li>
                              ))}
                              {mdPreview.warnings.length > 8 && <li>…and more</li>}
                            </ul>
                          </div>
                        )}
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="text-sm text-base-content/80">
                            {mdImportChecked.filter(Boolean).length} of {mdPreview.items.length} selected for import
                          </span>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs"
                              onClick={() => setMdImportChecked(mdPreview.items.map(() => true))}
                            >
                              Select all
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs"
                              onClick={() => setMdImportChecked(mdPreview.items.map(() => false))}
                            >
                              Select none
                            </button>
                          </div>
                        </div>
                        <div className="max-h-80 overflow-y-auto rounded-lg border border-base-300 divide-y divide-base-300 bg-base-100">
                          {mdPreview.items.map((it, i) => (
                            <label
                              key={i}
                              className="flex items-start gap-3 px-3 py-2 hover:bg-base-200/60 cursor-pointer text-sm"
                            >
                              <input
                                type="checkbox"
                                className="checkbox checkbox-sm mt-0.5 shrink-0"
                                checked={!!mdImportChecked[i]}
                                onChange={() => toggleMdImportChecked(i)}
                              />
                              <span className="min-w-0 flex-1 leading-snug">
                                <span className="font-mono text-xs text-base-content/60 mr-1">{i + 1}.</span>
                                <span className="break-words">{it.title}</span>
                                {it.preamble ? (
                                  <span className="badge badge-warning badge-xs ml-2 align-middle whitespace-nowrap">
                                    Preamble
                                  </span>
                                ) : null}
                              </span>
                            </label>
                          ))}
                        </div>
                        <button
                          type="button"
                          className={`btn btn-primary ${mdImportBusy ? 'btn-disabled' : ''}`}
                          disabled={mdImportBusy || !mdPreview.items.length || !mdImportChecked.some(Boolean)}
                          onClick={handleImportMarkdown}
                        >
                          {mdImportBusy ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                              Importing…
                            </>
                          ) : (
                            `Import ${mdImportChecked.filter(Boolean).length} ${mdImportChecked.filter(Boolean).length === 1 ? 'policy' : 'policies'}`
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {managementPanel === 'batches' && (
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain pr-1 [scrollbar-gutter:stable]">
                <div className="card bg-base-100 border border-base-300 shadow-sm max-w-4xl">
                  <div className="card-body">
                    <h3 className="card-title text-lg">
                      <FontAwesomeIcon icon={faTags} className="text-primary" />
                      Imported batches &amp; batch tags
                    </h3>
                    <p className="text-sm text-base-content/70">
                      Batch tag is the badge shown on every policy from that import. Several imports can share the same tag text. Clear the field and save to remove the badge.
                    </p>
                    {importBatches.length === 0 ? (
                      <div className="text-sm text-base-content/70 mt-2">
                        No import batches yet. Use the Import panel to bring in a Markdown file.
                      </div>
                    ) : (
                      <div className="mt-2 grid gap-3 md:grid-cols-2">
                        {importBatches.map((b) => (
                          <div key={b.importBatchId} className="bg-base-200 rounded-lg p-3 text-sm space-y-2">
                            <div className="min-w-0">
                              <div className="font-medium truncate">{b.sourceFileName || 'Imported policies'}</div>
                              <div className="text-base-content/60">{b.count} policies · batch {b.importBatchId.slice(0, 8)}…</div>
                            </div>
                            <label className="form-control w-full">
                              <span className="label-text text-xs">Batch tag (shared label / “folder”)</span>
                              <div className="flex flex-wrap gap-2 mt-1">
                                <input
                                  type="text"
                                  className="input input-bordered input-sm flex-1 min-w-[12rem]"
                                  value={
                                    b.importBatchId in batchTagEdits
                                      ? batchTagEdits[b.importBatchId]
                                      : (importBatchTags[b.importBatchId] ?? '')
                                  }
                                  onChange={(e) =>
                                    setBatchTagEdits((prev) => ({ ...prev, [b.importBatchId]: e.target.value }))
                                  }
                                  placeholder="e.g. Employee handbook"
                                  disabled={batchTagSavingId === b.importBatchId}
                                />
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm"
                                  disabled={resourceSaving || batchTagSavingId === b.importBatchId}
                                  onClick={() => handleSaveImportBatchTag(b.importBatchId)}
                                >
                                  {batchTagSavingId === b.importBatchId ? (
                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                  ) : (
                                    'Save tag'
                                  )}
                                </button>
                              </div>
                            </label>
                            <div className="flex justify-end">
                              <button
                                type="button"
                                className="btn btn-error btn-sm btn-outline"
                                disabled={resourceSaving || !!batchTagSavingId}
                                onClick={() => handleDeleteImportBatch(b)}
                              >
                                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                                Delete batch
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
      <Footer />
      <ArticleModal open={modalOpen} onClose={handleCloseModal} article={selectedArticle || undefined} />
      <ContactArticleModal open={contactModalOpen} onClose={handleCloseContactModal} contact={selectedContact || undefined} />
    </div>
  )
}

interface ResourcesPageProps {
}

const ResourcesPage: React.FC<ResourcesPageProps> = () => (
  <ResourcesSearchProvider>
    <ResourcesPageContent />
  </ResourcesSearchProvider>
)

export default ResourcesPage 